import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Dispatch, SetStateAction } from 'react';
import SampleFormControls from './SampleFormControls';
import { SampleSchema } from '@/zod_schemas';
import { getSampleColumns } from './sample/getSampleColumns';

const CustomNoRowsOverlay = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      fontSize: 16,
      color: 'gray',
    }}
  >
    No data found
  </Box>
);

interface AddSampleDialogProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  selectedRows: SampleSchema[];
  setSelectedRows: Dispatch<SetStateAction<SampleSchema[]>>;
  handleConfirmClick: () => void;
  handleClearSelection: () => void;
  sampleListData: SampleSchema[];
  isSubmitting: boolean;
}

const AddSampleDialog = ({
  open,
  setOpen,
  selectedRows,
  setSelectedRows,
  handleConfirmClick,
  isSubmitting,
  handleClearSelection,
  sampleListData,
}: AddSampleDialogProps) => {
  const columnsSelect = getSampleColumns({
    deleteAction: false,
  });

  const handleClose = () => {
    if (!isSubmitting) {
      setOpen(false);
    }
  };
  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        disableEscapeKeyDown={isSubmitting}
      >
        <DialogContent>
          <Box
            sx={{
              p: 3,
              marginTop: '2rem',
              backgroundColor: 'background.default',
              borderRadius: 3,
              boxShadow: 3,
              mx: 'auto',
              position: 'relative',
              opacity: isSubmitting ? 0.7 : 1,
              pointerEvents: isSubmitting ? 'none' : 'auto',
            }}
          >
            {/* Loading Overlay */}
            {isSubmitting && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: (theme) =>
                    alpha(theme.palette.background.paper, 0.88),
                  borderRadius: 3,
                  zIndex: 1000,
                }}
              >
                <Stack alignItems="center" spacing={2}>
                  <CircularProgress size={50} />
                  <Typography
                    variant="body1"
                    color="primary"
                    fontWeight="medium"
                  >
                    Assign samples...
                  </Typography>
                </Stack>
              </Box>
            )}

            <Typography variant="h5" mb={2} color="primary">
              Available samples
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              mb={2}
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <Box sx={{ flexGrow: 1, maxWidth: '500px' }}>
                <SampleFormControls />
              </Box>

              {selectedRows.length > 0 && (
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleConfirmClick()}
                    disabled={isSubmitting}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : null
                    }
                  >
                    {isSubmitting
                      ? 'Adding...'
                      : `Add Selected (${selectedRows.length})`}
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={() => handleClearSelection()}
                    disabled={isSubmitting}
                  >
                    Clear Selected ({selectedRows.length})
                  </Button>
                </Stack>
              )}
            </Stack>

            <Box
              sx={{
                height: 400,
                backgroundColor: 'background.paper',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                '& .MuiDataGrid-root': {
                  border: 'none',
                  fontFamily: 'Roboto, sans-serif',
                  flex: 1, // fill remaining height
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'primary.light',
                  fontWeight: 'bold',
                },
              }}
            >
              <DataGrid
                rows={sampleListData || []}
                columns={columnsSelect}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 5,
                    },
                  },
                }}
                pageSizeOptions={[5]}
                checkboxSelection
                disableRowSelectionOnClick
                rowSelectionModel={selectedRows.map((row) => row.id!)}
                onRowSelectionModelChange={(ids) => {
                  if (!isSubmitting) {
                    const selectedIDs = new Set(ids);
                    const selectedRowsData = (sampleListData || []).filter(
                      (row) => row.id && selectedIDs.has(row.id),
                    );
                    setSelectedRows(selectedRowsData);
                  }
                }}
                getRowId={(row) => row.id!}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlay,
                }}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddSampleDialog;
